import React, { useEffect, useState } from "react";
import { Play, Pause, Square, Lock, Unlock, Wrench, RotateCcw, Trophy, CalendarClock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { formatIST } from "@/components/Countdown";

const ACTIONS = [
  { key: "start", label: "Start Competition", icon: Play, color: "bg-[#138808] text-white", confirm: "Start the competition now? Discord webhook will fire." },
  { key: "pause", label: "Pause All Participants", icon: Pause, color: "bg-amber-500 text-white", confirm: "Pause the competition for ALL participants?" },
  { key: "resume", label: "Resume Competition", icon: Play, color: "bg-[#138808] text-white", confirm: "Resume the competition?" },
  { key: "end", label: "End Competition", icon: Square, color: "bg-red-600 text-white", confirm: "END the competition? No new answers will be accepted." },
  { key: "lock", label: "Lock Competition", icon: Lock, color: "bg-slate-800 text-white", confirm: "Lock the competition?" },
  { key: "schedule", label: "Back to Scheduled", icon: CalendarClock, color: "bg-[#003366] text-white", confirm: "Return competition to scheduled mode?" },
  { key: "open-registrations", label: "Open Registrations", icon: Unlock, color: "bg-[#003366] text-white" },
  { key: "close-registrations", label: "Lock New Registrations", icon: Lock, color: "bg-red-600 text-white", confirm: "Stop new registrations?" },
  { key: "maintenance-on", label: "Maintenance Mode ON", icon: Wrench, color: "bg-red-600 text-white", confirm: "Enable maintenance mode? The quiz will be disabled." },
  { key: "maintenance-off", label: "Maintenance Mode OFF", icon: Wrench, color: "bg-[#003366] text-white" },
  { key: "publish-results", label: "Publish Results", icon: Trophy, color: "bg-[#FF9933] text-white", confirm: "Make results public?" },
  { key: "hide-results", label: "Hide Results", icon: Trophy, color: "bg-slate-800 text-white" },
  { key: "reset-leaderboard", label: "Reset Leaderboard", icon: RotateCcw, color: "bg-red-700 text-white", confirm: "DELETE all attempts and answers for EVERYONE? This cannot be undone." },
];

export default function Control() {
  const [config, setConfig] = useState(null);
  const [sched, setSched] = useState({ date: "", start_time: "", interval_minutes: 30 });

  const load = async () => {
    try {
      const { data } = await api.get("/public/config");
      setConfig(data);
      setSched({ date: data.competition.date, start_time: data.competition.start_time, interval_minutes: data.competition.interval_minutes });
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const act = async (a) => {
    if (a.confirm && !window.confirm(a.confirm)) return;
    try {
      await api.post(`/admin/competition/${a.key}`);
      toast.success(`${a.label} — done`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveSchedule = async () => {
    try {
      await api.post("/admin/competition-schedule", { ...sched, interval_minutes: Number(sched.interval_minutes) });
      toast.success("Schedule updated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const statusColor = { scheduled: "bg-slate-100 text-slate-600", live: "bg-[#138808]/10 text-[#138808]", paused: "bg-amber-100 text-amber-700", ended: "bg-red-100 text-red-700", locked: "bg-slate-800 text-white" };

  return (
    <div data-testid="admin-control">
      <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tighter">Competition Control Center</h1>
      <p className="text-sm text-slate-500 mt-1">Dangerous actions require confirmation and are audit-logged.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">Current Status</div>
          <div className="mt-1 flex items-center gap-3">
            <span data-testid="control-status" className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full ${statusColor[config?.competition?.status] || "bg-slate-100"}`}>{config?.competition?.status || "…"}</span>
            {config?.competition?.maintenance_mode && <span className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-red-100 text-red-700">Maintenance</span>}
            {!config?.competition?.registration_open && <span className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-amber-100 text-amber-700">Registrations Closed</span>}
          </div>
        </div>
        <div className="text-sm text-slate-500">
          Starts: <b className="text-slate-800">{config ? formatIST(config.competition.start_at) : "…"}</b> • Interval: <b className="text-slate-800">{config?.competition?.interval_minutes} min</b>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="font-heading font-bold text-slate-900 mb-4">Schedule (IST)</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Date</label><Input data-testid="sched-date" type="date" className="mt-1.5" value={sched.date} onChange={(e) => setSched({ ...sched, date: e.target.value })} /></div>
          <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Start Time</label><Input data-testid="sched-time" type="time" className="mt-1.5" value={sched.start_time} onChange={(e) => setSched({ ...sched, start_time: e.target.value })} /></div>
          <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Category Interval (min)</label><Input data-testid="sched-interval" type="number" min={1} className="mt-1.5" value={sched.interval_minutes} onChange={(e) => setSched({ ...sched, interval_minutes: e.target.value })} /></div>
          <button onClick={saveSchedule} data-testid="sched-save" className="py-2.5 rounded-lg bg-[#003366] text-white text-sm font-bold hover:bg-[#00284f] transition-colors">Save Schedule</button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {ACTIONS.map((a) => (
          <button key={a.key} onClick={() => act(a)} data-testid={`control-${a.key}`}
            className={`${a.color} rounded-xl px-4 py-4 text-sm font-bold flex items-center gap-2.5 hover:opacity-90 transition-opacity shadow-sm`}>
            <a.icon className="w-4 h-4 shrink-0" /> {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}
